import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, MapPin } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Enquiry sent!");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-primary relative overflow-hidden" data-testid="contact-section">
      <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-secondary/15 blur-3xl" />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <span className="font-monotech text-xs tracking-[0.25em] uppercase text-secondary">Get In Touch</span>
          <h2 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none text-white mt-4">
            Your wound,<br /><span className="font-serifaccent italic font-normal text-secondary">our care.</span>
          </h2>
          <p className="text-white/70 text-base md:text-lg leading-relaxed mt-6 max-w-md">
            Questions about Healseeal, bulk orders or clinical partnerships? Send us a note and our
            care team will respond within one business day.
          </p>
          <div className="mt-10 space-y-5">
            <a href="mailto:care@healseeal.com" className="flex items-center gap-4 text-white/85 hover:text-secondary transition-colors duration-200" data-testid="contact-email-link">
              <span className="w-10 h-10 rounded-full bg-white/10 grid place-items-center"><Mail className="w-4 h-4" /></span>
              care@healseeal.com
            </a>
            <div className="flex items-center gap-4 text-white/85">
              <span className="w-10 h-10 rounded-full bg-white/10 grid place-items-center"><Phone className="w-4 h-4" /></span>
              +91 98765 43210
            </div>
            <div className="flex items-center gap-4 text-white/85">
              <span className="w-10 h-10 rounded-full bg-white/10 grid place-items-center"><MapPin className="w-4 h-4" /></span>
              Healseeal Labs, Bengaluru, India
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="bg-white rounded-3xl p-7 md:p-9"
          data-testid="contact-form"
        >
          <div className="space-y-5">
            <div>
              <label className="font-monotech text-xs tracking-widest uppercase text-text-muted">Name</label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your full name"
                data-testid="contact-form-name"
                className="mt-2 rounded-none border-x-0 border-t-0 border-b-2 focus-visible:ring-0 focus-visible:border-primary px-0"
              />
            </div>
            <div>
              <label className="font-monotech text-xs tracking-widest uppercase text-text-muted">Email</label>
              <Input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com"
                data-testid="contact-form-email"
                className="mt-2 rounded-none border-x-0 border-t-0 border-b-2 focus-visible:ring-0 focus-visible:border-primary px-0"
              />
            </div>
            <div>
              <label className="font-monotech text-xs tracking-widest uppercase text-text-muted">Message</label>
              <Textarea
                required
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="How can we help?"
                data-testid="contact-form-message"
                className="mt-2 rounded-none border-x-0 border-t-0 border-b-2 focus-visible:ring-0 focus-visible:border-primary px-0 resize-none"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="contact-form-submit"
              className="w-full rounded-none bg-primary text-white hover:bg-accent h-12 text-sm font-semibold tracking-wide"
            >
              {loading ? "Sending…" : "Send Enquiry"}
            </Button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};
